import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaRobot, FaPaperPlane, FaLinkedin, FaTwitter, FaFacebook, FaMapMarkerAlt, FaGlobe, FaUsers } from 'react-icons/fa';
import './Messages.css';

const Messages = () => {
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiTyping, setAiTyping] = useState(false);
  const aiMessagesEndRef = useRef(null);

  const scrollAiToBottom = () => {
    aiMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollAiToBottom();
  }, [aiMessages, aiTyping]);

  const handleAiSubmit = async (e) => {
    e.preventDefault();
    if (!aiInput.trim() || aiTyping) return;

    const userMessage = aiInput.trim();
    setAiMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setAiInput('');
    setAiTyping(true);

    // Simulate AI response
    setTimeout(() => {
      const aiResponse = getAiResponse(userMessage);
      setAiMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
      setAiTyping(false);
    }, 1000);
  };

  const getAiResponse = (message) => {
    const lowerMessage = message.toLowerCase();

    if (lowerMessage.includes('job') || lowerMessage.includes('position') || lowerMessage.includes('opening')) {
      return "I can help you find jobs! You can browse available positions on our Jobs page. What type of role are you interested in?";
    } else if (lowerMessage.includes('company') || lowerMessage.includes('employer')) {
      return "You can explore companies on our Companies page. Each company has detailed profiles with job openings. Would you like help finding companies in a specific industry?";
    } else if (lowerMessage.includes('apply') || lowerMessage.includes('application')) {
      return "To apply for jobs, simply browse our job listings and click 'Apply Now' on positions that interest you. You can track all your applications in the 'My Applications' section. Need help with your application?";
    } else if (lowerMessage.includes('profile') || lowerMessage.includes('resume')) {
      return "Keep your profile updated! Go to your Profile page to add your skills, experience, and education. A complete profile increases your chances of getting noticed by employers.";
    } else if (lowerMessage.includes('salary') || lowerMessage.includes('pay')) {
      return "Salary information is typically included in job postings. You can also filter jobs by salary range on our Jobs page. Remember, salary can often be negotiated based on your experience and skills!";
    } else if (lowerMessage.includes('remote') || lowerMessage.includes('work from home')) {
      return "We have many remote job opportunities! Use the location filter on the Jobs page and select 'Remote' to see all work-from-home positions.";
    } else if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey')) {
      return "Hello! I'm here to help you with anything related to jobs, companies, applications, or your career. What would you like to know?";
    } else if (lowerMessage.includes('help')) {
      return "I can assist you with:\n• Finding jobs and companies\n• Understanding the application process\n• Tips for your profile\n• Information about salaries\n• Remote work opportunities\n\nWhat would you like help with?";
    } else {
      return "I'm your career assistant! I can help you with job searches, company information, application tips, and more. Feel free to ask me anything about your career journey!";
    }
  };

  return (
    <div className="font-sans flex flex-col">
      {/* ─── NEW FLOATING APP LAYOUT ─── */}
      <div className="msg-page-wrapper">
        <div className="msg-app-container">
          
          {/* ─── LEFT PANEL: Abstract Teal Sidebar ─── */}
          <div className="msg-sidebar">
            {/* Background Orbs */}
            <div className="msg-orb-1"></div>
            <div className="msg-orb-2"></div>
            
            <div className="msg-avatar-wrapper">
              <FaRobot className="msg-avatar-icon" />
            </div>

            <h1 className="msg-title">AI Career<br/>Assistant</h1>
            <p className="msg-subtitle">
              Your intelligent guide to career growth. Get instant answers about jobs, companies, and application strategies.
            </p>

            <div className="msg-info-card">
              <div className="msg-info-header">
                <div className="msg-dot"></div>
                SYSTEM ONLINE
              </div>
              <p style={{ fontSize: '0.85rem', opacity: 0.8, lineHeight: 1.5 }}>
                Powered by Naukri Platform Intelligence. Our AI understands your career needs and helps navigate the job market efficiently.
              </p>
            </div>
          </div>

          {/* ─── RIGHT PANEL: Chat Area ─── */}
          <div className="msg-chat-area">
            
            {/* Header */}
            <div className="msg-chat-header">
              <div className="msg-header-title">Active Session</div>
              <div className="msg-header-status">
                <div className="msg-dot" style={{ width: '6px', height: '6px' }}></div>
                Private Conversation
              </div>
            </div>

            {/* Chat Scroll Container */}
            <div className="msg-scroll-container">
              
              {/* Empty State */}
              {aiMessages.length === 0 && (
                <div className="msg-empty-state">
                  <div className="msg-empty-icon">
                    <FaRobot style={{ fontSize: '3rem', color: '#00c2ab' }} />
                  </div>
                  <h3 className="msg-empty-title">How can I help?</h3>
                  <p className="msg-empty-text">
                    Start a conversation to get personalized career advice, job recommendations, and instant answers to your questions.
                  </p>
                </div>
              )}

              {/* Chat Bubbles */}
              {aiMessages.map((msg, index) => (
                <div key={index} className={`msg-bubble-wrapper ${msg.role === 'user' ? 'msg-bubble-user' : 'msg-bubble-ai'}`}>
                  {msg.role === 'assistant' && (
                    <div className="msg-bubble-avatar">
                      <FaRobot />
                    </div>
                  )}
                  <div className="msg-bubble-content">
                    {msg.content}
                  </div>
                </div>
              ))}

              {/* Typing Indicator */}
              {aiTyping && (
                <div className="msg-bubble-wrapper msg-bubble-ai">
                  <div className="msg-bubble-avatar">
                    <FaRobot />
                  </div>
                  <div className="msg-bubble-content msg-typing">
                    <div className="msg-typing-dot"></div>
                    <div className="msg-typing-dot"></div>
                    <div className="msg-typing-dot"></div>
                  </div>
                </div>
              )}
              
              {/* Scroll Anchor */}
              <div ref={aiMessagesEndRef} style={{ height: '1px' }}></div>
            </div>

            {/* Floating Input Bar */}
            <div className="msg-input-wrapper">
              <form onSubmit={handleAiSubmit} className="msg-input-form max-w-4xl mx-auto">
                <input
                  type="text"
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  placeholder="Ask anything about your career..."
                  disabled={aiTyping}
                  className="msg-input-field"
                />
                <button
                  type="submit"
                  disabled={!aiInput.trim() || aiTyping}
                  className="msg-submit-btn"
                >
                  <FaPaperPlane style={{ marginLeft: '-2px' }} />
                </button>
              </form>
            </div>

          </div>
        </div>
      </div>

      {/* ===================== CUSTOM TEAL FOOTER ===================== */}
      <footer className="relative overflow-hidden shrink-0 w-full" style={{ background: 'linear-gradient(135deg, #009e8e 0%, #00c2ab 100%)' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-[100px] pointer-events-none -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-[100px] pointer-events-none translate-x-1/2 translate-y-1/2"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 pb-12 border-b border-white/20">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                  <span className="text-white font-black text-lg">N</span>
                </div>
                <span className="text-white font-extrabold text-xl tracking-tight">Naukri Platform</span>
              </div>
              <p className="text-white/80 text-sm leading-relaxed font-medium">
                Your complete platform for networking, growth, and building a better future.
              </p>
              <div className="flex items-center gap-3 mt-6">
                {[FaLinkedin, FaTwitter, FaFacebook].map((Icon, i) => (
                  <a key={i} href="#" className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 border border-white/20 flex items-center justify-center text-white transition-all duration-300 hover:scale-110" onClick={(e) => e.preventDefault()}>
                    <Icon className="text-sm" />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">Quick Links</h4>
              <ul className="space-y-3">
                {[
                  { label: 'Browse Jobs', to: '/jobs' },
                  { label: 'Companies', to: '/companies' },
                  { label: 'Pricing', to: '/plans' },
                  { label: 'Post a Job', to: '/post-job' },
                ].map(link => (
                  <li key={link.label}>
                    <Link to={link.to} className="text-white/75 hover:text-white text-sm font-medium transition-colors duration-200 hover:underline underline-offset-4">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* For Employers */}
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">For Employers</h4>
              <ul className="space-y-3">
                {[
                  { label: 'Post a Job', to: '/post-job' },
                  { label: 'Employer Dashboard', to: '/employer-dashboard' },
                  { label: 'Create Company Profile', to: '/create-company' },
                  { label: 'Browse Candidates', to: '/jobs' },
                ].map(link => (
                  <li key={link.label}>
                    <Link to={link.to} className="text-white/75 hover:text-white text-sm font-medium transition-colors duration-200 hover:underline underline-offset-4">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">Contact Us</h4>
              <ul className="space-y-3 text-sm text-white/80 font-medium">
                <li className="flex items-start gap-3">
                  <FaMapMarkerAlt className="mt-0.5 text-white/60 shrink-0" />
                  <span>123 Business Street, Tech City, TC 12345</span>
                </li>
                <li className="flex items-center gap-3">
                  <FaGlobe className="text-white/60 shrink-0" />
                  <span>info@teztecch.com</span>
                </li>
                <li className="flex items-center gap-3">
                  <FaUsers className="text-white/60 shrink-0" />
                  <span>Mon – Fri: 9:00 AM – 6:00 PM</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
            <p className="text-white/70 font-medium">© 2026 Teztecch. All rights reserved.</p>
            <div className="flex items-center gap-6 text-white/70 font-medium">
              <Link to="/about" className="hover:text-white transition-colors">About Us</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
      {/* ===================== END CUSTOM FOOTER ===================== */}
    </div>
  );
};

export default Messages;
